import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface ViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  data: Record<string, any>;
  fields: Array<{
    key: string;
    label: string;
    type?: string;
    render?: (value: any, item: any) => React.ReactNode;
  }>;
}

export const ViewModal: React.FC<ViewModalProps> = ({
  isOpen,
  onClose,
  title,
  data,
  fields
}) => {
  if (!isOpen) return null;

  const formatValue = (value: any, type?: string) => {
    if (value === null || value === undefined) return 'N/A';
    
    switch (type) {
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'datetime':
        return new Date(value).toLocaleString();
      default:
        return String(value);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--color-border)]">
          <h3 className="text-lg font-semibold text-[var(--color-surface-text)]">{title}</h3>
          <button
            onClick={onClose}
            className="text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((field) => (
              <div key={field.key} className="space-y-1">
                <label className="block text-sm font-medium text-[var(--color-surface-text)]">
                  {field.label}
                </label>
                <div className="text-sm text-[var(--color-surface-text)] bg-[var(--color-secondary-bg)] p-3 rounded-md">
                  {field.render 
                    ? field.render(data[field.key], data)
                    : formatValue(data[field.key], field.type)
                  }
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-[var(--color-border)] bg-[var(--color-secondary-bg)]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-[var(--color-surface-text)] bg-[var(--color-surface-bg)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-secondary-bg)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--color-info)]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
